import { useEffect, useRef } from "react";

const SCROLL_LOCK_MS = 420;

/**
 * Hero scroll passthrough without touching pointer-events during mouse follow.
 * Cross-origin iframe swallows wheel — during an active scroll burst we briefly
 * disable the iframe so wheel over the embed still moves the page.
 */
export default function HeroSplineWheelBridge({ enabled = true, sectionRef }) {
  const pointerRef = useRef({ x: 0, y: 0 });
  const scrollLockUntilRef = useRef(0);
  const scrollLockTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const section = sectionRef?.current;
    if (!section) return undefined;

    const iframe = section.querySelector(".ds-hero-spline-bg-embed");

    const pointInHero = (x, y) => {
      const rect = section.getBoundingClientRect();
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    };

    const isClickableTarget = (target) =>
      target instanceof Element && Boolean(target.closest(".ds-hero-clickable"));

    const syncPointer = (x, y) => {
      pointerRef.current.x = x;
      pointerRef.current.y = y;
    };

    const setScrollLock = (active) => {
      section.classList.toggle("ds-hero-section--scroll-lock", active);
      if (iframe instanceof HTMLIFrameElement) {
        iframe.style.pointerEvents = active ? "none" : "auto";
      }
    };

    const extendScrollLock = () => {
      scrollLockUntilRef.current = Date.now() + SCROLL_LOCK_MS;
      setScrollLock(true);

      if (scrollLockTimerRef.current) {
        window.clearTimeout(scrollLockTimerRef.current);
      }

      scrollLockTimerRef.current = window.setTimeout(() => {
        if (Date.now() < scrollLockUntilRef.current) return;
        setScrollLock(false);
        scrollLockTimerRef.current = null;
      }, SCROLL_LOCK_MS);
    };

    const scrollPage = (event) => {
      event.preventDefault();
      window.scrollBy({
        top: event.deltaY,
        left: event.deltaX,
        behavior: "auto",
      });
    };

    const onPointerMove = (event) => {
      syncPointer(event.clientX, event.clientY);
    };

    const onWheel = (event) => {
      if (Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
        syncPointer(event.clientX, event.clientY);
      }

      const { x, y } = pointerRef.current;
      if (!pointInHero(x, y)) return;
      if (isClickableTarget(event.target)) return;

      extendScrollLock();
      scrollPage(event);
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: false, capture: true });

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("wheel", onWheel, { capture: true });
      if (scrollLockTimerRef.current) {
        window.clearTimeout(scrollLockTimerRef.current);
      }
      setScrollLock(false);
    };
  }, [enabled, sectionRef]);

  return null;
}
